import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { Text, HStack, VStack } from "@chakra-ui/react";
import { SearchFilter, SearchFilterType } from "gql/graphql";
import UserTagGroupSelector, {
  Option,
} from "modules/selectors/user-tag-group-selector";
import { MailPersonIcon } from "modules/icons";
import { useUsers } from "modules/providers/users-provider";

interface IProps {
  newSearchFilters: SearchFilter[];
  setNewSearchFilters: React.Dispatch<React.SetStateAction<SearchFilter[]>>;
}

const FiltersDrawerEmailSelector = ({
  newSearchFilters,
  setNewSearchFilters,
}: IProps) => {
  const { usersById } = useUsers();

  const getOptionValues = useCallback(
    (type: SearchFilterType): Option[] => {
      const filter = newSearchFilters.find((filter) => filter.type === type);
      if (!filter || !filter.objectIdListValue) return [];
      if (type === SearchFilterType.EmailUserIdList) {
        return filter.objectIdListValue.map((id) => ({
          id,
          type: "user",
          active: usersById[id]?.active ?? false,
        }));
      }
      return [];
    },
    [newSearchFilters, usersById],
  );

  const emailUserValues = useMemo<Option[]>(
    () => [...getOptionValues(SearchFilterType.EmailUserIdList)],
    [getOptionValues],
  );

  const onEmailUsersSelectorChanged = useCallback(
    (options: Option[]) => {
      setNewSearchFilters((prev) => {
        const _prev = [...prev].filter(
          (filter) => filter.type !== SearchFilterType.EmailUserIdList,
        );
        _prev.push({
          type: SearchFilterType.EmailUserIdList,
          objectIdListValue: options.map((user) => user.id),
        });
        return _prev;
      });
    },
    [setNewSearchFilters],
  );

  return (
    <VStack w="full">
      <HStack w="full">
        <MailPersonIcon fill={colors.blackSecondary} boxSize={5} />
        <Text
          fontFamily="Outfit"
          color={colors.blackSecondary}
          fontSize={18}
          fontWeight={600}
        >
          Email Users
        </Text>
      </HStack>
      <UserTagGroupSelector
        types={[]}
        includeEmailUsers
        isMultiSelect
        onSelectedOptionsChanged={onEmailUsersSelectorChanged}
        allowInactive
        value={emailUserValues}
        placeHolder="Enter email address"
      />
    </VStack>
  );
};

export default memo(FiltersDrawerEmailSelector);
